import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  type ContactUsType,
  getContactUsAllData,
  getContactUsMetaData,
  saveOrUpdateContact,
  type ContactUsMetaData,
  getContactDetailsById,
  deleteContact,
  type ContactUsGetAllResponse,
  getContactViewDetailsById,
  type ContactUsAllResponse,
} from '../services/ContactUsService';

export interface ContactListQueryPerams {
  userId: string;
  facilityId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
}

export const useGetContactUsMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ContactUsMetaData>({
      queryKey: ['CategoryMetaData', userId],
      queryFn: async () => {
        const data = await getContactUsMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    });

  return {
    CategoryMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveOrUpdateContact = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    ContactUsType,
    Error,
    ContactUsType & { userId: string; action: string; facilityId: string }
  >({
    mutationFn: async ({ userId, action, facilityId, ...payload }) => {
      const data = await saveOrUpdateContact(
        userId,
        action,
        facilityId,
        payload
      );
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ContactResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error saving or updating Contact:', error);
    },
  });
};

export const useGetContactUsAllData = ({
  userId,
  facilityId,
  page = 0,
  size = 10,
  sortField,
  sortType,
}: ContactListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ContactUsGetAllResponse>({
      queryKey: [
        'CategoryAllData',
        userId,
        facilityId,
        page,
        size,
        sortField,
        sortType,
      ],
      queryFn: async () => {
        const data = await getContactUsAllData(
          userId,
          facilityId,
          page,
          size,
          sortField,
          sortType
        );
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!facilityId,
    });

  return {
    CategoryAllData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetContactDetailsById = (userId: string, contactId: number) => {
  console.log('ids--', userId, contactId);
  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<ContactUsType>({
      queryKey: ['ContactDetailsById', userId, contactId],
      queryFn: async () => {
        const data = await getContactDetailsById(userId, contactId);
        return data;
      },
      // staleTime: 0,
      // gcTime: 0,
      enabled: !!contactId,
    });

  return {
    ContactDetailsById: data,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  };
};

export const useDeleteContact = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    ContactUsType,
    Error,
    { userId: string; contactId: number }
  >({
    mutationFn: async ({ userId, contactId }) => {
      const data = await deleteContact(userId, contactId);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ContactUsResponse'],
      });
      onSuccess();
    },
  });
};

export const useGetContactViewDetailsById = (
  userId: string,
  contactId: number
) => {
  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<ContactUsAllResponse>({
      queryKey: ['ContactDetailsViewById', userId, contactId],
      queryFn: async () => {
        const data = await getContactViewDetailsById(userId, contactId);
        return data;
      },
      // staleTime: 0,
      // gcTime: 0,
      enabled: !!contactId,
    });

  return {
    ContactDetailsViewById: data,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  };
};
